import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, LogOut, Lock, Search } from "lucide-react";
import { http, apiError, imgUrl, formatPrice, timeAgo } from "../lib/api";
import AdminMembers from "../components/site/AdminMembers";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const TOKEN_KEY = "bazaar_admin_token";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await http.post("/auth/admin/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-sm mx-auto px-4 py-16">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="admin-login-form">
        <div className="flex items-center gap-2 text-[var(--navy)]"><Lock className="w-5 h-5 text-[var(--orange)]" /><h1 className="font-display font-bold text-2xl">Admin Login</h1></div>
        <input className="field" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
        <input className="field" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        <button disabled={busy} className="w-full h-12 rounded-xl bg-[var(--navy)] text-white font-semibold disabled:opacity-60" data-testid="admin-login-button">{busy ? "..." : "Login"}</button>
      </form>
    </div>
  );
}

function DeleteButton({ ad, onDeleted }) {
  const del = async () => {
    try { await http.delete(`/admin/ads/${ad.id}`, auth()); toast.success("Ad deleted"); onDeleted(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="h-9 px-3 rounded-lg bg-red-50 text-[var(--sell)] hover:bg-red-100 text-sm font-semibold flex items-center gap-1" data-testid={`admin-delete-${ad.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this ad?</AlertDialogTitle>
          <AlertDialogDescription>"{ad.title}" will be removed from the site permanently.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={del} className="bg-[var(--sell)] hover:bg-[#c62828]" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    http.get("/admin/stats", auth()).then((r) => setStats(r.data)).catch((e) => e.response?.status === 401 && onLogout());
    http.get("/admin/ads", { params: { q: query || undefined, page }, ...auth() }).then((r) => setData(r.data));
  }, [query, page, onLogout]);
  useEffect(() => { load(); }, [load]);
  const [tab, setTab] = useState("ads");

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-3xl text-[var(--navy)]">Admin · Control Panel</h1>
        <button onClick={onLogout} className="flex items-center gap-1 text-sm text-slate-600 hover:text-[var(--sell)]" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
      <div className="mt-5 grid grid-cols-3 md:grid-cols-6 gap-3">
        {[["Live Ads", stats?.total, "admin-stat-total"], ["Hidden (expired)", stats?.hidden, "admin-stat-hidden"], ["Last 24h", stats?.last24, "admin-stat-24h"], ["Deleted", stats?.deleted, "admin-stat-deleted"], ["Members", stats?.members, "admin-stat-members"], ["Active Plans", stats?.active_members, "admin-stat-active"]].map(([l, v, t]) => (
          <div key={t} className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">{l}</p><p className="font-display font-extrabold text-2xl text-[var(--navy)]" data-testid={t}>{v ?? "—"}</p></div>
        ))}
      </div>
      <div className="mt-5 flex gap-2">
        {[["ads", "Ads"], ["members", "Members & Plans"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`h-10 px-4 rounded-lg font-semibold ${tab === k ? "bg-[var(--navy)] text-white" : "bg-white border border-slate-200"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "members" ? <div className="mt-4"><AdminMembers auth={auth} onChange={load} /></div> : <>
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(q.trim()); }} className="mt-5 flex gap-2">
        <input className="field" placeholder="Search by keyword..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
        <button className="h-12 px-4 rounded-lg bg-[var(--navy)] text-white" data-testid="admin-search-button"><Search className="w-5 h-5" /></button>
      </form>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 divide-y" data-testid="admin-ads-list">
        {data?.items.map((ad) => (
          <div key={ad.id} className="flex items-center gap-3 p-3" data-testid={`admin-row-${ad.id}`}>
            <div className="w-16 h-12 rounded-md bg-slate-100 overflow-hidden shrink-0">{ad.photos[0] && <img src={imgUrl(ad.photos[0])} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <Link to={`/ad/${ad.id}`} className="font-semibold text-[var(--navy)] truncate block hover:underline">{ad.title}</Link>
              <p className="text-xs text-slate-500 truncate">{formatPrice(ad.price)} · {ad.category} · {ad.taluka || ad.location}, {ad.location} · {ad.mobile} · {timeAgo(ad.created_at)} · <b className={ad.is_visible ? "text-green-700" : "text-[var(--sell)]"}>{ad.is_visible ? "Live" : "Hidden"}</b></p>
            </div>
            <DeleteButton ad={ad} onDeleted={load} />
          </div>
        ))}
        {data && data.items.length === 0 && <p className="p-6 text-center text-slate-500">No ads found</p>}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-4 flex justify-center gap-3 items-center text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 px-3 border rounded-lg bg-white disabled:opacity-40" data-testid="admin-prev-page">Prev</button>
          <span>{page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="h-9 px-3 border rounded-lg bg-white disabled:opacity-40" data-testid="admin-next-page">Next</button>
        </div>
      )}
      </>}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return setAuthed(false);
    http.get("/auth/admin/me", auth()).then(() => setAuthed(true)).catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }, []);
  if (authed === null) return <div className="py-20 text-center text-slate-500">Loading...</div>;
  return authed ? <Dashboard onLogout={logout} /> : <LoginForm onLogin={() => setAuthed(true)} />;
}
